/**
 * Source-type share of all citations, as a donut (2026-09-08).
 *
 * Lisa's ask, verbatim (2026-09-01): "if it's like source type, I think what
 * would be helpful is to have a pie chart of all the source types to begin with.
 * How much is supplier content? How much is peer reviewed?" — replacing a bar
 * chart that, in Ramos's words, was "not collecting the aggregate of the actual
 * collection, it's just giving a bar chart of the line item".
 *
 * WHY A PIE IS ACTUALLY CORRECT HERE, when it usually is not: every cited URL is
 * classified into exactly ONE source category, so the slices genuinely sum to a
 * whole. Part-to-whole is the donut's one legitimate job. It is capped at six
 * segments (the `dataviz` ceiling for at-a-glance part-to-whole) with the tail
 * folded into "Other" — never by generating more hues.
 *
 * CLICK TO EXPAND (Lisa, 2026-09-08, on this exact chart: "I like this, and
 * then within each section, if we clicked on it and it opened it up ... answers
 * citing the source and total citations ... top five maybe"). Clicking a wedge
 * or its legend row opens that type's top five cited hosts beneath the donut —
 * from the per-type domain breakdown the aggregate already stores.
 *
 * PALETTE: categorical slots 1-6, dark steps, assigned in descending-share order
 * so that adjacent slices always take adjacent slots. Direct labels + a valued
 * legend ship as the secondary encoding regardless, so identity is never
 * colour-alone.
 */
import { useState } from 'react';
import type { SourceShareSlice } from '@aurora/shared';
import { CATEGORICAL } from './palette';

const SIZE = 190;
const R_OUT = 84;
const R_IN = 50;
const CX = SIZE / 2;
const CY = SIZE / 2;
/** Surface-coloured gap between segments (the 2px spacer rule). */
const GAP_DEG = 1.1;

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function arcPath(startDeg: number, endDeg: number): string {
  const large = endDeg - startDeg > 180 ? 1 : 0;
  const [x1, y1] = polar(CX, CY, R_OUT, startDeg);
  const [x2, y2] = polar(CX, CY, R_OUT, endDeg);
  const [x3, y3] = polar(CX, CY, R_IN, endDeg);
  const [x4, y4] = polar(CX, CY, R_IN, startDeg);
  return [
    `M ${x1} ${y1}`,
    `A ${R_OUT} ${R_OUT} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${R_IN} ${R_IN} 0 ${large} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
}

const pct = (v: number): string => {
  const p = v * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${Math.round(p)}%`;
};

export function SourceDonut({
  slices,
  caption,
}: {
  slices: SourceShareSlice[];
  caption?: string;
}): JSX.Element | null {
  const [open, setOpen] = useState<string | null>(null);
  const lead = slices[0];
  if (!lead) return null;

  const total = slices.reduce((n, s) => n + s.citations, 0);

  let cursor = 0;
  const arcs = slices.map((s, i) => {
    const sweep = s.share * 360;
    const start = cursor;
    const end = cursor + sweep;
    cursor = end;
    // Only inset a gap when the slice is wide enough to survive it.
    const inset = sweep > GAP_DEG * 3 ? GAP_DEG / 2 : 0;
    return {
      slice: s,
      // "Other" always takes the last slot rather than a generated hue — the
      // tail is not a series.
      color: CATEGORICAL[Math.min(i, CATEGORICAL.length - 1)] as string,
      d: arcPath(start + inset, Math.max(start + inset, end - inset)),
      canOpen: (s.domains?.length ?? 0) > 0,
    };
  });

  const toggle = (label: string, canOpen: boolean): void => {
    if (!canOpen) return;
    setOpen((cur) => (cur === label ? null : label));
  };
  const opened = arcs.find((a) => a.slice.label === open) ?? null;
  const drill = opened?.slice.domains ?? [];
  const drillMax = Math.max(1, ...drill.map((d) => d.citations ?? d.appearances));

  return (
    <figure className="source-donut-wrap">
      <div className="source-donut">
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label="Share of all citations by source type"
        >
          {arcs.map((a) => (
            <path
              key={a.slice.label}
              d={a.d}
              fill={a.color}
              opacity={open && open !== a.slice.label ? 0.45 : 1}
              className={a.canOpen ? 'donut-arc can-open' : 'donut-arc'}
              onClick={() => toggle(a.slice.label, a.canOpen)}
            >
              <title>
                {a.slice.label}: {a.slice.citations} citations (
                {pct(a.slice.share)})
                {a.canOpen ? ' — click for the top sources' : ''}
              </title>
            </path>
          ))}
          {/* Hero figure in the hole — the one number the chart is making. */}
          <text
            x={CX}
            y={CY - 6}
            textAnchor="middle"
            className="donut-center-value"
          >
            {pct(lead.share)}
          </text>
          <text
            x={CX}
            y={CY + 12}
            textAnchor="middle"
            className="donut-center-label"
          >
            {lead.label.length > 16
              ? `${lead.label.slice(0, 15)}…`
              : lead.label}
          </text>
        </svg>

        {/* The legend doubles as the table view: label, share and raw count —
            and as the control: a row with a breakdown behind it is a button. */}
        <ul className="donut-legend">
          {arcs.map((a) => {
            const inner = (
              <>
                <span
                  className="chart-legend-swatch"
                  style={{ background: a.color }}
                />
                <span className="donut-legend-label">
                  {a.slice.label}
                  {a.canOpen && (
                    <span className="donut-legend-caret" aria-hidden="true">
                      {open === a.slice.label ? ' ▾' : ' ▸'}
                    </span>
                  )}
                </span>
                <span className="donut-legend-pct">{pct(a.slice.share)}</span>
                <span className="donut-legend-count">{a.slice.citations}</span>
              </>
            );
            return (
              <li
                key={a.slice.label}
                className={
                  a.canOpen
                    ? open === a.slice.label
                      ? 'has-drill is-open'
                      : 'has-drill'
                    : undefined
                }
              >
                {a.canOpen ? (
                  <button
                    type="button"
                    className="donut-legend-btn"
                    onClick={() => toggle(a.slice.label, true)}
                    aria-expanded={open === a.slice.label}
                    title={`Show the top sources cited under ${a.slice.label}`}
                  >
                    {inner}
                  </button>
                ) : (
                  inner
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {opened && drill.length > 0 && (
        <div className="donut-drill">
          <h4>
            <span>Top sources cited under {opened.slice.label}</span>
            <button
              type="button"
              className="donut-drill-close"
              onClick={() => setOpen(null)}
            >
              close
            </button>
          </h4>
          <table className="leads-table donut-drill-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Answers citing it</th>
                <th>Total citations</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {drill.map((d) => {
                const v = d.citations ?? d.appearances;
                return (
                  <tr key={d.domain}>
                    <td>{d.domain}</td>
                    <td>{d.appearances}</td>
                    <td>{d.citations ?? '—'}</td>
                    <td className="donut-drill-barcell">
                      <span className="standing-track">
                        <span
                          className="standing-bar"
                          style={{
                            width: `${Math.max(2, (v / drillMax) * 100)}%`,
                            background: opened.color,
                          }}
                        />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <figcaption className="muted source-donut-caption">
        {caption ??
          `Share of all ${total} cited links, by the type of source AI drew on. Each cited link is classified into exactly one type, so these add to 100%. Click a type to see the sources behind it.`}
      </figcaption>
    </figure>
  );
}
